import {applyRoundingDiscount} from '@artemis-group/aiw-contracts';
/**
 * Step 2.2 — priced options, and the line between the two copies.
 *
 * One engine result, two projections:
 *
 *   · **staff**  — sell, material, labour, contribution, COD basis, warnings and
 *                  the $0 lines that were considered. Never customer-facing.
 *   · **customer** — sell prices only, produced by `projectCustomerPricing`, whose
 *                  whitelist has no field for a cost, a margin, a supplier or a
 *                  price basis. A range reads *from $418.47* with its notice; a $0
 *                  line is dropped; an undecided colour reads *Customer not yet
 *                  decided*.
 *
 * The engine is asked once, through `/api/pricing-base/preview`. No figure on this
 * screen is arithmetic done in the browser.
 *
 * One engine behaviour needs a decision made visible rather than a code change:
 * while ANY line is a colour range the engine returns `material` null for all of
 * them, which would blank the margin on the whole quote over ~$68 of price
 * uncertainty. Jacky, 2026-09-16: show the decided lines priced and mark the rest
 * as undecided. That is what `decided` below does — and it says so on the screen.
 */
import {useEffect,useMemo,useRef,useState} from 'react';
import {Button} from '@aiw/ui';
import {useQuery} from '@tanstack/react-query';
import {UNDECIDED_COLOUR,gmLabel,projectCustomerPricing,ProposalPricing,marginLabel,type CustomerPricing,type Margin, type ProposalServiceLine, type ProposalAccessory} from '@artemis-group/aiw-contracts';
import {colourText,noticeText} from '@aiw/i18n/react';
import {previewPricing,type PricedResult} from '../lib/proposal-pricing';
import {inputCls} from './job-bits';
import {committedNumber} from './number-field';
import {proposalProfitability} from '../lib/proposal-profitability';
import {useT} from '../lib/i18n';
import {money} from '../lib/format';
import {proposalPriceBasis} from '../lib/carpet-priced-basis';

/** The one place the engine is called, shared by the rail and the Share tab. */
export function useProposalPricing(pricing: ProposalPricing | undefined, work?: {services?: ProposalServiceLine[]; accessories?: ProposalAccessory[];roundingDiscount?:number}) {
  // The API now needs AIW's own fields — the rate he set, each line's group and role — because it
  // prices the RECOMMENDED selections as the quote and the OPTION pool separately at that rate. So
  // the request carries `ProposalPricing`, not the stripped `BasePricingInput`.
  const {t}=useT();
  const request = useMemo(() => {
    if (!pricing&&!work?.services?.length&&!work?.accessories?.length) return null;
    const parsed = ProposalPricing.safeParse(pricing??{products:[],services:[]});
    return parsed.success ? parsed.data : null;
  }, [pricing,work?.services?.length,work?.accessories?.length]);
  const requestKey = request ? JSON.stringify(request) : 'none';
  const [settled,setSettled] = useState({key:requestKey,request});

  // A proposal is an editor, not a search box. Repricing on every keypress made the whole result table
  // disappear and rebuild while staff were still entering a quantity or rate. Keep the edit local, then
  // ask the engine once after the person pauses. The old result remains visible while the new one arrives.
  useEffect(()=>{
    if(requestKey===settled.key)return;
    if(!request){setSettled({key:requestKey,request});return;}
    const timer=window.setTimeout(()=>setSettled({key:requestKey,request}),500);
    return()=>window.clearTimeout(timer);
  },[requestKey,request,settled.key]);
  const preview = useQuery({
    queryKey: ['pricing-preview', settled.key],
    enabled: !!settled.request,
    queryFn: () => previewPricing(settled.request),
    placeholderData: previous => previous,
    retry: false,
  });
  const projection = useMemo(() => {
    if (!preview.data || !settled.request) return null;
    // THE WORK GOES WITH THE PRODUCTS. Without this argument the services and accessories on the
    // proposal — removal, skirting, and the stair work the old platform quotes on 60% of jobs — were
    // priced into nothing: they never reached the customer's copy at all (found 18 Sep, by driving a
    // dummy proposal end to end).
    const projected=projectCustomerPricing(preview.data, settled.request, preview.data.options ?? [], settled.request.margin, {
      services: work?.services ?? [],
      accessories: work?.accessories ?? [],
    });
    // Always: the total ex GST is rounded down to the whole dollar, with or without a discount (16 Oct 2026).
    if(!projected.pricing)return projected;
    try{return {...projected,pricing:applyRoundingDiscount(projected.pricing,work?.roundingDiscount??0,t('commercial.roundingDiscount'))};}
    catch{return {...projected,pricing:null,blockers:[...projected.blockers,t('commercial.roundingTooLarge')]};}
  }, [preview.data, settled.request, work?.services, work?.accessories,work?.roundingDiscount,t]);
  return { engine: request, preview, projection, ready: settled.key===requestKey&&!preview.isFetching };
}

function PriceOverrideField({override,assisted,onCommit,label,hint}:{override:number|undefined;assisted:number|null;onCommit:(value:number|undefined)=>void;label:string;hint:string}){
  const shown=override??assisted;
  const external=shown===null?'':String(Math.round(shown*100)/100);
  const [draft,setDraft]=useState(external),[editing,setEditing]=useState(false),[dirty,setDirty]=useState(false);
  const cancelled=useRef(false);
  useEffect(()=>{if(!editing)setDraft(external);},[external,editing]);
  const commit=()=>{
    if(cancelled.current){cancelled.current=false;setEditing(false);setDirty(false);return;}
    // Moving through the form must not pin the engine's suggested rate as a manual override.
    // Only an actual edit crosses the boundary into proposal state.
    if(!dirty){setEditing(false);return;}
    const parsed=committedNumber(draft);
    if(parsed===null){setDraft(external);setEditing(false);setDirty(false);return;}
    setDraft(parsed===undefined?'':String(parsed));
    setEditing(false);
    setDirty(false);
    if(parsed!==override)onCommit(parsed);
  };
  return <input className={`${inputCls} w-24 text-right`} type="number" inputMode="decimal" min="0" step="any"
    aria-label={label} title={hint} value={draft}
    onFocus={()=>{setEditing(true);setDirty(false);}} onChange={e=>{setDraft(e.target.value);setDirty(true);}} onBlur={commit}
    onKeyDown={e=>{if(e.key==='Enter')e.currentTarget.blur();if(e.key==='Escape'){cancelled.current=true;setDraft(external);e.currentTarget.blur();}}}/>;
}

export function PricedOptions({ pricing, result, options, isPending, error, projection, onChoose, onAdjustPrice, carpetProductIds=new Set<string>() }: {
  pricing: ProposalPricing | undefined;
  result: PricedResult | undefined;
  /** The option pool, already priced at the proposal's rate by the API. Never in the total. */
  options: PricedResult['lines'];
  isPending: boolean;
  error: Error | null;
  projection: { pricing: CustomerPricing | null; blockers: string[]; zeroLines: string[] } | null;
  carpetProductIds?: ReadonlySet<string>;
  /** Make the assisted price HIS: per line, per unit. The engine prices it, he adjusts it, and the GM%
   *  both ways is what tells him whether the adjustment is one he wants. */
  onAdjustPrice: (selectionId: string, perUnit: number | undefined) => void;
  /** Make an alternative the recommended line, PINNING the price it was listed at. This is the step the
   *  engine's `sellOverride` exists for: once chosen, the line is repriced as the recommended one, and
   *  the number the customer was shown has to be the number they get. */
  onChoose: (selectionId: string, pinnedPerUnit: number) => void;
}) {
  const {t} = useT();
  const lines = result?.lines ?? [];
  const gm = result?.gm ?? null;
  // The staff table's product lines line up with the RECOMMENDED selections (the options are priced in
  // their own call), which is what makes an inline price adjustment land on the right line.
  const recommended = (pricing?.products ?? []).filter(p => p.role !== 'option');
  const decided = lines.filter(l => l.revenue !== null && l.material !== null);
  const undecided = lines.filter(l => l.revenue === null || l.material === null);
  const blocked = undecided.filter(l => l.revenue === null);
  const materialKnown = decided.reduce((n, l) => n + (l.material ?? 0), 0);
  const labourKnown = decided.reduce((n, l) => n + (l.labour ?? 0), 0);
  const revenueKnown = decided.reduce((n, l) => n + (l.revenue ?? 0), 0);
  const profitability = proposalProfitability(lines,gm);
  const hasSelection = (pricing?.products.length ?? 0) > 0;

  if (!hasSelection) return <section className="rounded-lg border bg-card p-5"><h2 className="font-display text-sm font-semibold">{t('priced.title')}</h2><p className="mt-2 text-sm text-muted-foreground">{t('priced.empty')}</p></section>;

  return <div className="space-y-4">
    {result && <section className="rounded-lg border border-gold/40 bg-card p-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-sm font-semibold">{t('priced.profit.title')}</h2>
        <span className="text-micro text-muted-foreground">{t('priced.profit.staffOnly')}</span>
      </header>
      {profitability.pricedLines>0?<>
        <dl className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Figure label={t('priced.profit.externalProfit')} value={money(profitability.externalProfit)} />
          <Figure label={t('priced.profit.externalMargin')} value={gmLabel(profitability.externalMargin)} />
          <Figure label={t('priced.profit.crewProfit')} value={money(profitability.crewProfit)} />
          <Figure label={t('priced.profit.crewMargin')} value={gmLabel(profitability.crewMargin)} />
        </dl>
        <p className="mt-2 text-micro text-muted-foreground">{profitability.complete?t('priced.profit.complete'):t('priced.profit.partial',{priced:profitability.pricedLines,total:profitability.totalLines})}</p>
      </>:<p className="mt-3 text-sm text-muted-foreground">{t('priced.profit.waiting')}</p>}
    </section>}
    <section className="rounded-lg border bg-card">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5">
        <h2 className="font-display text-sm font-semibold">{t('priced.staffTitle')}</h2>
        <span className="text-micro text-muted-foreground">{isPending?t('priced.updating'):t('priced.staffNote')}</span>
      </header>
      {isPending&&!result&&<p className="p-4 text-sm">{t('priced.pricing')}</p>}
      {error && <p role="alert" className="p-4 text-sm">{t('priced.failed', {message: error.message})}</p>}
      {result && <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-micro text-muted-foreground"><tr className="border-b">
            <th className="px-3 py-2 text-left">{t('priced.col.line')}</th><th className="px-3 py-2 text-right">{t('common.quantity')}</th>
            <th className="px-3 py-2 text-right">{t('priced.col.rate')}</th><th className="px-3 py-2 text-right">{t('priced.col.yourPrice')}</th><th className="px-3 py-2 text-right">{t('common.material')}</th><th className="px-3 py-2 text-right">{t('common.labour')}</th><th className="px-3 py-2 text-right">{t('priced.gm.column')}</th>
            <th className="px-3 py-2 text-left">{t('priced.col.notes')}</th>
          </tr></thead>
          <tbody>
            {lines.map((line, index) => {const selected=recommended[index];const basis=proposalPriceBasis(line,selected,!!selected&&carpetProductIds.has(selected.productId));return <tr key={`${line.label}:${index}`} className="border-b align-top">
              <td className="px-3 py-2">{line.label}{line.costBasis === 'COD' && <span className="ml-2 rounded border px-1 text-micro">{t('priced.codStaffOnly')}</span>}</td>
              <td className="num px-3 py-2 text-right">{basis?`${basis.quantity.toFixed(2)} ${basis.unit}`:t('priced.rollWidthNeeded')}</td>
              {/* The rate, not only the total: "I just a total prices, I cannot see the base price
                  offered". The engine returns a line total (revenue) and never a unit rate, so it is
                  shown as the total over the quantity — the same figure the customer is being
                  offered per unit, and the one a person checks against the price book. */}
              <td className="num px-3 py-2 text-right">{basis?unitRate(line.revenue,basis.quantity,basis.unit):'—'}</td>
              <td className="num px-3 py-2 text-right">{recommended[index]&&basis ? <PriceOverrideField
                  override={recommended[index]!.sellOverride===undefined?undefined:recommended[index]!.sellOverride/basis.width}
                  assisted={line.revenue !== null&&basis.quantity?line.revenue/basis.quantity:null}
                  label={`${t('priced.assistedPrice')} / ${basis.unit}`}
                  hint={t('priced.assistedPriceHint')}
                  onCommit={value=>onAdjustPrice(recommended[index]!.id,value===undefined?undefined:value*basis.width)}
                /> : recommended[index]&&!basis?t('priced.rollWidthNeeded'):(line.revenue !== null ? money(line.revenue) : line.revenueRange ? `${money(line.revenueRange.min)}–${money(line.revenueRange.max)}` : '—')}{recommended[index] && basis && (line.revenue !== null || recommended[index]!.sellOverride != null) && <span className="block text-micro text-muted-foreground">{t('priced.assistedTotal', {amount: money(line.revenue ?? 0)})}</span>}</td>
              <td className="num px-3 py-2 text-right text-gold">{line.material !== null ? money(line.material) : t('priced.notYetDecided')}</td>
              <td className="num px-3 py-2 text-right text-gold">{line.labour !== null ? money(line.labour) : '—'}</td>
              <td className="num px-3 py-2 text-right">{gm?.lines[index] ? <span title={t('priced.gm.title')} className="font-medium">{gmLabel(gm.lines[index]!.external.gm)}{gm.lines[index]!.bonus !== null && gm.lines[index]!.bonus > 0 ? <span className="ml-1 text-micro text-muted-foreground">+{gmLabel(gm.lines[index]!.bonus)}</span> : null}</span> : '—'}</td>
              <td className="px-3 py-2 text-micro">{[...(line.notices ?? []).map(n => noticeText(t, n)), ...line.warnings].join(' · ') || '—'}{line.revenue === 0 && <span className="ml-2 rounded border px-1">{t('priced.zeroDropped')}</span>}</td>
            </tr>})}
          </tbody>
          <tfoot>
            <tr className="border-t"><td className="px-3 py-2 font-medium" colSpan={3}>{result.subtotal === null && result.subtotalRange ? t('priced.subtotalFrom') : t('common.subtotal')}</td>
              <td className="num px-3 py-2 text-right font-medium">{result.subtotal !== null ? money(result.subtotal) : result.subtotalRange ? `${money(result.subtotalRange.min)}–${money(result.subtotalRange.max)}` : '—'}</td>
              <td className="num px-3 py-2 text-right text-gold">{result.internal.material !== null ? money(result.internal.material) : t('priced.incomplete')}</td>
              <td className="num px-3 py-2 text-right text-gold">{result.internal.externalLabour !== null ? money(result.internal.externalLabour) : t('priced.incomplete')}</td>
              <td className="num px-3 py-2 text-right font-medium">{gm ? gmLabel(gm.external.gm) : '—'}</td>
              <td className="px-3 py-2 text-micro">+ GST</td>
            </tr>
          </tfoot>
        </table>
      </div>}
    </section>

    {pricing && <OptionsPool pricing={pricing} lines={options} margin={result?.margin ?? null} onChoose={onChoose} carpetProductIds={carpetProductIds} />}

    {result && <details className="rounded-lg border bg-card p-4">
      <summary className="cursor-pointer font-display text-sm font-semibold">{t('priced.internalTitle')}</summary>
      {gm && <div className="mt-3 rounded border border-l-4 border-l-gold p-3">
        <p className="font-medium">{t('priced.gm.headline', {gm: gmLabel(gm.external.gm)})}</p>
        <p className="mt-1 text-sm">{t('priced.gm.bonus', {gm: gmLabel(gm.artemisCrew.gm), points: gmLabel(gm.bonus)})}</p>
        <p className="mt-1 text-micro text-muted-foreground">{t('priced.gm.note')}</p>
      </div>}
      {blocked.length === 0 && result.internal.artemisContribution !== null ? <dl className="mt-3 grid gap-3 sm:grid-cols-4">
        <Figure label={t('common.subtotal')} value={money(result.subtotal)} />
        <Figure label={t('common.material')} value={money(result.internal.material)} />
        <Figure label={t('priced.externalLabour')} value={money(result.internal.externalLabour)} />
        <Figure label={t('priced.contribution')} value={money(result.internal.artemisContribution)} />
      </dl> : <div className="mt-3 space-y-3">
        <p className="text-sm">{t('priced.decidedBlurb')}</p>
        <dl className="grid gap-3 sm:grid-cols-4">
          <Figure label={t(decided.length === 1 ? 'priced.decidedSell' : 'priced.decidedSellPlural', {count: decided.length})} value={money(revenueKnown)} />
          <Figure label={t('common.material')} value={money(materialKnown)} />
          <Figure label={t('common.labour')} value={money(labourKnown)} />
          <Figure label={t('priced.contribution')} value={money(revenueKnown - materialKnown - labourKnown)} />
        </dl>
        <ul className="space-y-1 text-sm">{undecided.map((l, i) => <li key={i}>{t('priced.excluded')} <b>{l.label}</b> — {l.warnings[0] ?? t('priced.notPriced')}</li>)}</ul>
        <p className="text-micro text-muted-foreground">{t('priced.decidedNote')}</p>
      </div>}
      {result.warnings.length > 0 && <ul className="mt-3 space-y-1 text-micro">{result.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
    </details>}

    {projection && <details className="rounded-lg border bg-card p-4">
      <summary className="cursor-pointer font-display text-sm font-semibold">{t('priced.customerTitle')}</summary>
      <header className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-micro text-muted-foreground">{projection.pricing ? t('priced.customerExact') : t('priced.customerNotReleasable')}</span>
      </header>
      {projection.blockers.length > 0 && <div role="alert" className="mt-3 rounded border p-3 text-sm">
        <p className="font-medium">{t('priced.blockedTitle')}</p>
        <ul className="mt-1 list-disc pl-5">{projection.blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>
        <p className="mt-2 text-micro text-muted-foreground">{t('priced.blockedNote')}</p>
      </div>}
      {projection.pricing && <div className="mt-3 space-y-3">
        <ul className="divide-y">
          {projection.pricing.lines.map((line, index) => <li key={`${line.label}:${index}`} className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-sm">
            <span>{line.label}{line.colour && <span className="text-muted-foreground"> · {colourText(t, line.colour, UNDECIDED_COLOUR)}</span>}<span className="text-micro text-muted-foreground"> · {line.quantity.toFixed(2)} {line.unit}{line.unitSell !== null ? ` @ ${money(line.unitSell)}/${line.unit}` : ''}</span></span>
            <span className="num">{line.sell !== null ? <>{line.unitSell !== null ? `${money(line.unitSell)}/${line.unit} · ` : ''}<b>{money(line.sell)}</b></> : line.sellRange ? t('common.from', {amount: money(line.sellRange.min)}) : '—'}</span>
          </li>)}
        </ul>
        <dl className="grid gap-3 border-t pt-3 sm:grid-cols-3">
          <Figure label={t('common.subtotal')} tone="customer" value={projection.pricing.subtotal !== null ? money(projection.pricing.subtotal) : projection.pricing.subtotalRange ? t('common.from', {amount: money(projection.pricing.subtotalRange.min)}) : t('common.toBeConfirmed')} />
          <Figure label={t('common.gst')} tone="customer" value={projection.pricing.gst !== null ? money(projection.pricing.gst) : t('common.toBeConfirmed')} />
          <Figure label="Total ex GST" tone="customer" value={projection.pricing.subtotal !== null ? `${money(projection.pricing.subtotal)} + GST` : projection.pricing.subtotalRange ? `${t('common.from', {amount: money(projection.pricing.subtotalRange.min)})} + GST` : t('common.toBeConfirmed')} />
        </dl>
        {(projection.pricing.optionGroups ?? []).length > 0 && <div className="space-y-3 border-t pt-3">
          <h3 className="font-display text-sm font-semibold">{t('priced.options.title')}</h3>
          {projection.pricing.optionGroups.map(g => <div key={g.group}>
            <p className="text-sm font-medium">{g.group}</p>
            <ul className="divide-y">{g.options.map((o, i) => <li key={`${o.label}:${i}`} className="flex flex-wrap items-baseline justify-between gap-2 py-1.5 text-sm">
              <span>{o.label}{o.colour ? <span className="text-muted-foreground"> · {o.colour}</span> : null}<span className="text-micro text-muted-foreground"> · {o.quantity.toFixed(2)} {o.unit}{o.unitSell !== null ? ` @ ${money(o.unitSell)}/${o.unit}` : ''}</span></span>
              <span className="num">{o.sell !== null ? money(o.sell) : '—'}</span>
            </li>)}</ul>
          </div>)}
        </div>}
        {projection.pricing.notices.map((n, i) => <p key={i} className="text-sm">{noticeText(t, n)}</p>)}
        {projection.zeroLines.length > 0 && <p className="text-micro text-muted-foreground">{t('priced.zeroNote', {lines: projection.zeroLines.join(', ')})}</p>}
      </div>}
    </details>}
  </div>;
}

/** The rate behind a total: total ÷ quantity, with the line's own unit. Null-safe, and it says
 *  nothing rather than "$Infinity" when a line has no quantity yet. */
function unitRate(total: number | null, quantity: number, unit: string): string {
  if (total === null || !quantity) return '—';
  return `${money(total / quantity)}/${unit}`;
}

function Figure({ label, value, tone = 'staff' }: { label: string; value: string; tone?: 'staff' | 'customer' }) {
  return <div><dt className="text-micro text-muted-foreground">{label}</dt><dd className={`num font-display text-lg font-semibold ${tone === 'staff' ? 'text-gold' : ''}`}>{value}</dd></div>;
}

/** The options the customer may choose, grouped by sub-category, at the rate he set. */
function OptionsPool({ pricing, lines, margin, onChoose, carpetProductIds }: { pricing: ProposalPricing; lines: PricedResult['lines']; margin: Margin | null; onChoose: (selectionId: string, pinnedPerUnit: number) => void; carpetProductIds:ReadonlySet<string> }) {
  const {t} = useT();
  const optionSelections = pricing.products.filter(p => p.role === 'option');
  if (optionSelections.length === 0) return null;
  const groups = [...new Set(optionSelections.map(p => p.group || t('pricing.line.noGroup')))];
  return <section className="rounded-lg border bg-card p-4">
    <header className="flex flex-wrap items-baseline justify-between gap-2">
      <h2 className="font-display text-sm font-semibold">{t('priced.options.title')}</h2>
      <span className="text-micro text-muted-foreground">{margin ? t('priced.options.listedAt', {rate: marginLabel(margin)}) : t('pricing.margin.none')}</span>
    </header>
    <p className="mt-1 text-micro text-muted-foreground">{t('priced.options.notInTotal')} {t('priced.options.swapNote')}</p>
    <div className="mt-3 space-y-3">
      {groups.map(group => <div key={group}>
        <h3 className="text-sm font-medium">{group}</h3>
        <ul className="mt-1 divide-y">
          {optionSelections.filter(p => (p.group || t('pricing.line.noGroup')) === group).map((selection, i) => {
            const line = lines[optionSelections.indexOf(selection)];
            const basis=line?proposalPriceBasis(line,selection,carpetProductIds.has(selection.productId)):null;
            return <li key={selection.id} className="flex flex-wrap items-baseline justify-between gap-2 py-1.5 text-sm">
              <span>{line?.label ?? selection.productId}{selection.colour ? <span className="text-muted-foreground"> · {colourText(t, selection.colour, UNDECIDED_COLOUR)}</span> : null}
                <span className="text-micro text-muted-foreground"> · {basis?`${basis.quantity.toFixed(2)} ${basis.unit}`:t('priced.rollWidthNeeded')}</span></span>
              <span className="flex items-center gap-2">
                <span className="num">{line?.revenue != null&&basis ? <>{basis.quantity>0 ? `${money(line.revenue/basis.quantity)}/${basis.unit} · ` : ''}<b>{money(line.revenue)}</b></> : (line?.warnings[0] ?? t('priced.notYetDecided'))}</span>
                {line?.revenue != null && basis && line.quantity > 0 && <Button size="sm" variant="outline" onClick={() => onChoose(selection.id, line.revenue! / line.quantity)}>{t('priced.options.choose')}</Button>}
              </span>
            </li>;
          })}
        </ul>
      </div>)}
    </div>
  </section>;
}

/** The figure the Share step needs: a selling copy, or the reason there is none. */
export function customerRelease(projection: { pricing: CustomerPricing | null; blockers: string[] } | null) {
  if (!projection) return { pricing: undefined as CustomerPricing | undefined, blocked: 'Price the offer before releasing it to the customer.' };
  if (projection.blockers.length > 0) return { pricing: undefined, blocked: projection.blockers.join('; ') };
  return { pricing: projection.pricing ?? undefined, blocked: projection.pricing ? '' : 'Price the offer before releasing it to the customer.' };
}
